/**
 * Audience review API client (#667 / #668).
 *
 * Typed, **GET-only** wrappers over the four read-only review lists:
 *
 *   GET /api/acmirror/review/opted-out       — who has opted out, of what, and why
 *   GET /api/acmirror/review/not-in-ac       — who CE21 has that ActiveCampaign does not
 *   GET /api/acmirror/review/recently-added  — who wave 2 created, and in which batch (#454)
 *   GET /api/acmirror/review/held            — who wave 2 held back, and why (#454)
 *
 * There is deliberately NO mutate fetcher here. The opted-out list is a review
 * surface, never a re-subscribe tool (the standing guardrail: the ~951 opt-outs
 * are never re-subscribed), and creating the missing contacts is #598's job on
 * the backend, not a button on a list.
 *
 * The CSV download uses the same endpoint with `export=csv` and is fetched
 * directly rather than through `apiRequest` (which JSON-decodes the body),
 * mirroring `exportSegmentCsv` in `api/actions.ts`. Every download is audited
 * server-side.
 *
 * NOTE: like the rest of `acmirror`, these URLs have NO trailing slash.
 */
import { apiRequest } from "./client";
import { resolveTenant } from "../lib/tenant";
import type {
  HeldFilters,
  HeldPage,
  NotInACFilters,
  NotInACPage,
  OptedOutFilters,
  OptedOutPage,
  RecentlyAddedFilters,
  RecentlyAddedPage,
} from "./audienceReview.types";

/** Page size for the review lists (matches `AudienceReviewPagination`). */
export const AUDIENCE_REVIEW_PAGE_SIZE = 50;

/** The largest page the backend will serve (`AudienceReviewPagination.max_page_size`). */
export const AUDIENCE_REVIEW_MAX_PAGE_SIZE = 500;

/** The four review lists, by the path segment under `/api/acmirror/review/`. */
export type ReviewListPath = "opted-out" | "not-in-ac" | "recently-added" | "held";

const TENANT_HEADER = "X-Tenant";

/** Build the query string for a review request (blank values are omitted). */
function reviewParams(
  filters: Record<string, string | undefined>,
  page?: number,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  if (page && page > 1) params.set("page", String(page));
  return params;
}

/**
 * `GET /api/acmirror/review/opted-out` — every CE21-matched person whose AC
 * contact is unsubscribed from at least one list, with which list(s), the reason
 * AC recorded, the date, and their Master Contact List status. Filters compose,
 * so `{standing: "active", master: "unsubscribed", reason: "spam"}` is the
 * re-consent worklist in one request.
 */
export function getOptedOutReview(
  filters: OptedOutFilters = {},
  page = 1,
): Promise<OptedOutPage> {
  const params = reviewParams(filters as Record<string, string | undefined>, page);
  const qs = params.toString();
  return apiRequest<OptedOutPage>(
    `/acmirror/review/opted-out${qs ? `?${qs}` : ""}`,
  );
}

/**
 * `GET /api/acmirror/review/not-in-ac` — CE21 people with an email and no
 * mirrored AC contact, in three cohorts (members / vendors / non-members), each
 * flagged when a namesake already exists in AC under a different email. The
 * `summary.byGroup` totals always describe the WHOLE population, so switching
 * tab never changes the other tabs' counts.
 */
export function getNotInACReview(
  filters: NotInACFilters = {},
  page = 1,
): Promise<NotInACPage> {
  const params = reviewParams(filters as Record<string, string | undefined>, page);
  const qs = params.toString();
  return apiRequest<NotInACPage>(
    `/acmirror/review/not-in-ac${qs ? `?${qs}` : ""}`,
  );
}

/**
 * `GET /api/acmirror/review/recently-added` — the contacts wave 2 created
 * (#454): every still-live `AcCreatedContact` in the window (default the last
 * 30 days), newest first, with the batch it came from. `summary` describes the
 * whole window whatever `run` / `q` narrow the table to.
 */
export function getRecentlyAdded(
  filters: RecentlyAddedFilters = {},
  page = 1,
): Promise<RecentlyAddedPage> {
  const params = reviewParams(filters as Record<string, string | undefined>, page);
  const qs = params.toString();
  return apiRequest<RecentlyAddedPage>(
    `/acmirror/review/recently-added${qs ? `?${qs}` : ""}`,
  );
}

/**
 * `GET /api/acmirror/review/held` — the people wave 2 held back because a
 * namesake in ActiveCampaign is unsubscribed or ambiguous (#454). Empties as
 * `ac_wave create --release-held` creates them.
 */
export function getHeldForReview(
  filters: HeldFilters = {},
  page = 1,
): Promise<HeldPage> {
  const params = reviewParams(filters as Record<string, string | undefined>, page);
  const qs = params.toString();
  return apiRequest<HeldPage>(`/acmirror/review/held${qs ? `?${qs}` : ""}`);
}

/** The file name the backend attaches to each list's CSV. */
export function reviewCsvFilename(
  path: ReviewListPath,
  filters: Record<string, string | undefined>,
): string {
  switch (path) {
    case "opted-out":
      return "opted-out.csv";
    case "not-in-ac":
      return `not-in-activecampaign-${filters.group || "all"}.csv`;
    case "recently-added":
      return `recently-added-since-${filters.since || "default"}.csv`;
    case "held":
      return "held-for-review.csv";
  }
}

/**
 * Every email on one review list, under the same filters that produced the
 * on-screen table — the "Copy emails" control every People view carries. Pages
 * through at the backend's maximum page size so the clipboard holds the WHOLE
 * filtered list, not the 50 rows on screen. Blank addresses are dropped and
 * duplicates collapsed (a household address is one recipient).
 */
export async function collectReviewEmails(
  path: ReviewListPath,
  filters: Record<string, string | undefined> = {},
): Promise<string[]> {
  const seen = new Set<string>();
  const emails: string[] = [];
  let page = 1;
  for (;;) {
    const params = reviewParams(filters);
    params.set("page_size", String(AUDIENCE_REVIEW_MAX_PAGE_SIZE));
    if (page > 1) params.set("page", String(page));
    const payload = await apiRequest<{
      next: string | null;
      results: { email: string }[];
    }>(`/acmirror/review/${path}?${params.toString()}`);
    for (const row of payload.results) {
      const email = (row.email ?? "").trim();
      const key = email.toLowerCase();
      if (!email || seen.has(key)) continue;
      seen.add(key);
      emails.push(email);
    }
    if (!payload.next) return emails;
    page += 1;
  }
}

/**
 * Download one of the review lists as CSV (the file that goes to the client).
 * Returns the row total the backend reports in `X-Row-Total`. The same filters
 * that produced the on-screen list produce the file — and the same server-side
 * audit row records that it left the system.
 */
export async function downloadReviewCsv(
  path: ReviewListPath,
  filters: Record<string, string | undefined> = {},
): Promise<{ rowTotal: number }> {
  const params = reviewParams(filters);
  params.set("export", "csv");

  const response = await fetch(
    `/api/acmirror/review/${path}?${params.toString()}`,
    {
      method: "GET",
      headers: new Headers({ [TENANT_HEADER]: resolveTenant() }),
      credentials: "include",
    },
  );
  if (!response.ok) {
    const err = new Error(`Export failed: ${response.status}`) as Error & {
      status: number;
    };
    err.status = response.status;
    throw err;
  }

  const rowTotal = Number(response.headers.get("X-Row-Total") ?? "0");
  triggerDownload(await response.blob(), reviewCsvFilename(path, filters));
  return { rowTotal };
}

/** Trigger a browser download of `blob` under `filename` (no-op outside a DOM). */
export function triggerDownload(blob: Blob, filename: string): void {
  if (
    typeof document === "undefined" ||
    typeof URL.createObjectURL !== "function"
  ) {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
